import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Descriptions, Modal, Space, Spin, Table, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import * as api from '@apis/grouping-agent';
import {
  GroupingAgentRunItem,
  GroupingMode,
  RunItemDetailEnrollment,
  RunItemDetailGroup,
  ScheduleMaintenanceSummary,
} from '@models/grouping-agent';
import EnrollmentSourceDrawer from './EnrollmentSourceDrawer';

const { Text, Paragraph } = Typography;

const modeColor: Record<GroupingMode, string> = {
  full: 'blue',
  incremental: 'cyan',
  maintenance: 'purple',
  skipped: 'default',
};

const maintenanceLabels: { key: keyof ScheduleMaintenanceSummary; label: string }[] = [
  { key: 'prunedScheduleCount', label: 'Prune 제거' },
  { key: 'duplicateScheduleCount', label: '중복 배정' },
  { key: 'shortGroupCountBefore', label: 'Short 그룹(전)' },
  { key: 'dissolvedGroupCount', label: 'Dissolve' },
  { key: 'refullTriggered', label: 'Refull' },
];

interface Props {
  runId: string;
  runItem: GroupingAgentRunItem | null;
  open: boolean;
  onClose: () => void;
}

const RunItemDetailModal: React.FC<Props> = ({ runId, runItem, open, onClose }) => {
  const [sourceScheduleId, setSourceScheduleId] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ['grouping-agent-run-item-detail', runId, runItem?._id],
    queryFn: () => api.getGroupingAgentRunItemDetail(runId, runItem!._id),
    enabled: open && !!runItem?._id,
  });

  const detail = detailQuery.data?.data;

  const unassignedColumns: ColumnsType<RunItemDetailEnrollment> = [
    {
      title: '참가자',
      dataIndex: 'participantName',
      width: 140,
      render: (v, r) => (
        <Space size={4}>
          <span>{v || '-'}</span>
          {r.namePending && <Tag color="orange">이름 보충 필요</Tag>}
        </Space>
      ),
    },
    { title: '구매자', dataIndex: 'buyerName', width: 90, render: (v) => v || '-' },
    { title: '연락처', dataIndex: 'buyerPhone', width: 110, render: (v) => v || '-' },
    { title: '채널', dataIndex: 'sourceName', width: 70, render: (v) => v || '-' },
    {
      title: '옵션/회차',
      width: 200,
      ellipsis: true,
      render: (_, r) => r.optionName || r.scheduleName || '-',
    },
    { title: '주문번호', dataIndex: 'orderUid', width: 120, ellipsis: true, render: (v) => v || '-' },
    {
      title: '미배정 사유',
      dataIndex: 'unassignedReason',
      ellipsis: true,
      render: (v?: string) => v || <Text type="secondary">사유 없음</Text>,
    },
    {
      title: '',
      width: 88,
      render: (_, r) => (
        <Button size="small" onClick={() => setSourceScheduleId(r.scheduleId)}>
          원본 보기
        </Button>
      ),
    },
  ];

  const groupColumns: ColumnsType<RunItemDetailGroup> = [
    { title: '그룹명', dataIndex: 'name', width: 90 },
    {
      title: '인원',
      width: 120,
      render: (_, r) => `${r.currentCount}/${r.studentsMin}/${r.studentsMax < 0 ? '제한없음' : r.studentsMax}`,
    },
    {
      title: '상태',
      dataIndex: 'groupStatus',
      width: 80,
      render: (s: string) => <Tag color={s === 'short' ? 'red' : 'green'}>{s}</Tag>,
    },
    { title: '보충 필요', dataIndex: 'minNeeded', width: 80 },
    {
      title: '표시',
      width: 200,
      render: (_, r) => (
        <Space size={4} wrap>
          {(r.namePendingCount ?? 0) > 0 && <Tag color="orange">이름 보충 {r.namePendingCount}명</Tag>}
          {r.needsRecruitment && <Tag color="volcano">라스트찬스 +{r.minNeeded}명</Tag>}
        </Space>
      ),
    },
  ];

  const title = runItem ? `${runItem.productName} — ${runItem.optionName || '회차'}` : '회차 상세';

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      footer={null}
      width={960}
      destroyOnClose
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}>
      {detailQuery.isLoading && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      )}

      {detail && !detailQuery.isLoading && (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="수업일">
              {dayjs(detail.runItem.startDate).format('YYYY-MM-DD HH:mm')}
            </Descriptions.Item>
            <Descriptions.Item label="상태">
              <Tag>{detail.runItem.status}</Tag>
              {detail.runItem.groupingMode && (
                <Tag color={modeColor[detail.runItem.groupingMode]} style={{ marginLeft: 4 }}>
                  {detail.runItem.groupingMode}
                </Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="결제건">{detail.runItem.enrollmentCount}</Descriptions.Item>
            <Descriptions.Item label="환불위험">
              {detail.runItem.refundRiskBand ? <Tag>{detail.runItem.refundRiskBand}</Tag> : '-'}
            </Descriptions.Item>
            {detail.runItem.skipReason && (
              <Descriptions.Item label="스킵 사유" span={2}>
                {detail.runItem.skipReason}
              </Descriptions.Item>
            )}
            {detail.runItem.groupingModeReason && (
              <Descriptions.Item label="처리 사유" span={2}>
                {detail.runItem.groupingModeReason}
              </Descriptions.Item>
            )}
          </Descriptions>

          {detail.runItem.maintenanceSummary && (
            <div>
              <Text strong>멤버십 정리 (maintenance)</Text>
              <Descriptions bordered size="small" column={3} style={{ marginTop: 8 }}>
                {maintenanceLabels.map(({ key, label }) => {
                  const v = detail.runItem.maintenanceSummary![key];
                  const display = typeof v === 'boolean' ? (v ? '예' : '아니오') : v;
                  return (
                    <Descriptions.Item key={key} label={label}>
                      {display}
                    </Descriptions.Item>
                  );
                })}
              </Descriptions>
            </div>
          )}

          <div>
            <Text strong>생성 그룹 ({detail.groups.length}개)</Text>
            <Table
              size="small"
              rowKey="_id"
              style={{ marginTop: 8 }}
              columns={groupColumns}
              dataSource={detail.groups}
              pagination={false}
              locale={{ emptyText: '생성된 그룹 없음' }}
            />
          </div>

          <div>
            <Space>
              <Text strong>미배정 예약</Text>
              <Badge count={detail.unassigned.length} showZero color={detail.unassigned.length ? 'red' : 'default'} />
            </Space>
            <Table
              size="small"
              rowKey="scheduleId"
              style={{ marginTop: 8 }}
              columns={unassignedColumns}
              dataSource={detail.unassigned}
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
              locale={{ emptyText: '미배정 예약 없음' }}
            />
          </div>

          {detail.pendingMappingReviews.length > 0 && (
            <div>
              <Text strong>카카오 매핑 대기 ({detail.pendingMappingReviews.length}건)</Text>
              <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                매핑이 완료되지 않아 그룹핑에서 제외된 예약일 수 있습니다. 카카오 매핑 탭에서 승인하세요.
              </Paragraph>
              <Table
                size="small"
                rowKey="_id"
                dataSource={detail.pendingMappingReviews}
                pagination={false}
                columns={[
                  { title: 'priceName', dataIndex: 'priceName', ellipsis: true },
                  {
                    title: '신뢰도',
                    dataIndex: 'mappingConfidence',
                    width: 80,
                    render: (c?: string) => (c ? <Tag>{c}</Tag> : '-'),
                  },
                  { title: 'LLM 사유', dataIndex: 'llmReason', ellipsis: true },
                  {
                    title: '참가자',
                    render: (_, r) => r.enrollment?.participantName || '-',
                  },
                ]}
              />
            </div>
          )}

          {detail.llmAnalysis?.unassignedAnalysis && (
            <div>
              <Text strong>LLM 미배정 분석</Text>
              <Paragraph style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
                {detail.llmAnalysis.unassignedAnalysis}
              </Paragraph>
            </div>
          )}

          {detail.runItem.actionsLog && detail.runItem.actionsLog.length > 0 && (
            <div>
              <Text strong>처리 로그</Text>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                {detail.runItem.actionsLog.map((line, i) => (
                  <li key={i}>
                    <Text type="secondary">{line}</Text>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Space>
      )}

      <EnrollmentSourceDrawer
        scheduleId={sourceScheduleId}
        open={!!sourceScheduleId}
        onClose={() => setSourceScheduleId(null)}
      />
    </Modal>
  );
};

export default RunItemDetailModal;
